import type { DogId } from '../assets/names';
import { DOGS } from './dogs';
import { CAFE_ITEMS, type CafeItem } from './cafe';

/**
 * Save game and progression rules – pure functions on plain data (no
 * graphics, no storage), so they are unit-tested. save.ts persists them.
 */
export interface SaveData {
  version: 1;
  /** highest level the player may play */
  nextLevel: number;
  /** best star result per level id */
  bestStars: Record<number, number>;
  /** unspent stars (earned in levels, spent on the café) */
  stars: number;
  coins: number;
  boosters: { hammer: number; spoon: number; moves5: number };
  /** dogs that arrived: dirty until the next café upgrade, then happy */
  dogs: Partial<Record<DogId, 'rescued' | 'happy'>>;
  /** café items placed, in buying order */
  cafe: string[];
  /** processed store transactions (a purchase is never granted twice) and one-time packs */
  purchases: { tx: string[]; starter: boolean };
  /** owned cosmetics, e.g. 'bandana_paw' */
  cosmetics: string[];
  /** rewarded videos watched for shop coins today (daily limit) */
  adCoins: { day: string; count: number };
  /** how often / when the starter offer popped up (never after it was bought) */
  starterOffer: { shown: number; lastDay: string };
}

export type Booster = keyof SaveData['boosters'];

export type Unlock = 'cafe' | 'shop' | 'map' | 'paris';

export type ProgressEvent =
  | { type: 'unlock'; what: Unlock }
  | { type: 'dog'; dog: DogId }
  | { type: 'booster'; booster: Booster; amount: number };

export interface WinResult {
  save: SaveData;
  /** stars added to the balance (only improvements over the best result count) */
  starsEarned: number;
  coinsEarned: number;
  events: ProgressEvent[];
}

/** From which level each booster can be used, and the free amount given when it unlocks. */
export const BOOSTER_FROM: Record<Booster, { level: number; gift: number }> = {
  hammer: { level: 4, gift: 3 },
  spoon: { level: 8, gift: 2 },
  moves5: { level: 12, gift: 1 },
};

/** Features that open after winning a level. */
const UNLOCK_AFTER: Record<Unlock, number> = { cafe: 1, shop: 3, map: 5, paris: 18 };

export function newSave(): SaveData {
  return {
    version: 1, nextLevel: 1, bestStars: {}, stars: 0, coins: 0, boosters: { hammer: 0, spoon: 0, moves5: 0 }, dogs: {}, cafe: [],
    purchases: { tx: [], starter: false }, cosmetics: [], adCoins: { day: '', count: 0 }, starterOffer: { shown: 0, lastDay: '' },
  };
}

export const completed = (s: SaveData) => s.nextLevel - 1;

export function isUnlocked(s: SaveData, what: Unlock): boolean {
  return completed(s) >= UNLOCK_AFTER[what];
}

export function boosterAvailable(level: number, b: Booster): boolean {
  return level >= BOOSTER_FROM[b].level;
}

/**
 * Coins for a win: 10 / 25 / 40 for 1 / 2 / 3 stars. Kept modest on purpose so
 * that +5 moves (100 coins) stays a real decision in a close loss.
 */
export const coinsFor = (stars: number) => [0, 10, 25, 40][Math.max(0, Math.min(3, stars))];

export function recordWin(prev: SaveData, level: number, stars: number): WinResult {
  const s: SaveData = structuredClone(prev);
  const events: ProgressEvent[] = [];
  const best = s.bestStars[level] ?? 0;
  const starsEarned = Math.max(0, stars - best);
  s.bestStars[level] = Math.max(best, stars);
  s.stars += starsEarned;
  const coinsEarned = coinsFor(stars);
  s.coins += coinsEarned;

  const firstClear = level >= s.nextLevel;
  if (firstClear) {
    s.nextLevel = level + 1;
    for (const what of Object.keys(UNLOCK_AFTER) as Unlock[]) {
      if (UNLOCK_AFTER[what] === level) events.push({ type: 'unlock', what });
    }
    for (const b of Object.keys(BOOSTER_FROM) as Booster[]) {
      if (BOOSTER_FROM[b].level === level + 1) {
        s.boosters[b] += BOOSTER_FROM[b].gift;
        events.push({ type: 'booster', booster: b, amount: BOOSTER_FROM[b].gift });
      }
    }
    for (const d of DOGS) {
      if (d.arrivesAfterLevel === level && !s.dogs[d.id]) {
        // café dogs arrive dirty and get a home with the next café upgrade;
        // travel dogs (Coco) are met on the postcard, already happy
        s.dogs[d.id] = d.location === 'cafe' ? 'rescued' : 'happy';
        events.push({ type: 'dog', dog: d.id });
      }
    }
  }
  return { save: s, starsEarned, coinsEarned, events };
}

export function nextCafeItem(s: SaveData): CafeItem | null {
  return CAFE_ITEMS.find((i) => !s.cafe.includes(i.id)) ?? null;
}

export interface BuyResult {
  save: SaveData;
  item: CafeItem;
  /** a dirty rescued dog that got bathed and is happy now */
  cleaned: DogId | null;
}

/** Buys the next café item with stars; null if not affordable or all done. */
export function buyNextCafeItem(prev: SaveData): BuyResult | null {
  const item = nextCafeItem(prev);
  if (!item || prev.stars < item.cost) return null;
  const s: SaveData = structuredClone(prev);
  s.stars -= item.cost;
  s.cafe.push(item.id);
  const dirty = DOGS.find((d) => s.dogs[d.id] === 'rescued');
  if (dirty) s.dogs[dirty.id] = 'happy';
  return { save: s, item, cleaned: dirty?.id ?? null };
}

/** Uses one booster; false if none left. */
export function useBooster(s: SaveData, b: Booster): boolean {
  if (s.boosters[b] <= 0) return false;
  s.boosters[b]--;
  return true;
}

export function dogsFound(s: SaveData): DogId[] {
  return DOGS.filter((d) => s.dogs[d.id]).map((d) => d.id);
}
